import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Tabs, Tab, Box, Checkbox, TextField, Button, Paper } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  tabsRoot: {
    borderBottom: '2px solid #e0e0e0',
    marginBottom: '20px',
    '& .MuiTabs-indicator': {
      backgroundColor: '#0099b9',
      height: '2px',
    },
  },
  tab: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    minWidth: 'auto',
    padding: '10px 20px',
    backgroundColor: '#f8f9fa',
    '&.Mui-selected': {
      backgroundColor: 'white',
      color: '#0099b9',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '12px 8px',
      fontSize: '13px',
      minWidth: 0,
      flex: 1,
    },
  },
  // Desktop grid layout
  hoursGrid: {
    display: 'grid',
    gridTemplateColumns: '100px 1fr 1fr 100px',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '10px',
    padding: '8px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  hoursGridHeader: {
    fontWeight: 600,
    backgroundColor: '#f8f9fa',
    marginBottom: '5px',
  },
  dayLabel: {
    fontWeight: 500,
    color: '#555',
  },
  timeField: {
    '& input': {
      padding: '6px 10px',
      fontSize: '13px',
    },
  },
  intervalNote: {
    fontSize: '11px',
    color: '#666',
    fontStyle: 'italic',
    marginTop: '4px',
  },
  // Mobile card layout
  mobileContainer: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  dayCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid #e0e0e0',
  },
  dayCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  dayCardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#333',
  },
  timeRange: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    '@media (max-width: 480px)': {
      flexDirection: 'column',
      gap: '8px',
      alignItems: 'stretch',
    },
  },
  timeFieldMobile: {
    flex: 1,
    '& input': {
      padding: '12px',
      fontSize: '16px',
      backgroundColor: 'white',
      color: '#000',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#ddd',
      },
      '&.Mui-disabled': {
        backgroundColor: '#f0f0f0',
        '& input': {
          color: '#999',
        },
      },
    },
    '@media (max-width: 480px)': {
      width: '100%',
    },
  },
  timeSeparator: {
    fontWeight: 500,
    color: '#666',
    '@media (max-width: 480px)': {
      display: 'none',
    },
  },
  closedLabel: {
    fontSize: '14px',
    color: '#999',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '8px',
  },
  contactCard: {
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const HoursManagement = React.forwardRef(({ initialHoursData, activeServices, availableServices, onHoursChange }, ref) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [hoursData, setHoursData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const activeServiceIdSet = React.useMemo(() => {
    if (!activeServices || !activeServices.length) return new Set();
    return new Set(activeServices.map((s) => s.id));
  }, [activeServices]);

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    if (!availableServices || !availableServices.length) return;

    const updatedHours = {};
    availableServices.forEach(({ id }) => {
      if (initialHoursData?.[id]) {
        updatedHours[id] = { ...initialHoursData[id] };
        for (let day = 0; day <= 6; day++) {
          if (!updatedHours[id][day]) {
            updatedHours[id][day] = {
              intervals: [{ start_time: '09:00', end_time: '17:00', crosses_midnight: false }],
              is_closed_all_day: false
            };
          }
        }
      } else {
        updatedHours[id] = {};
        for (let day = 0; day <= 6; day++) {
          updatedHours[id][day] = { intervals: [], is_closed_all_day: true };
        }
      }
    });
    setHoursData(updatedHours);
  }, [initialHoursData, availableServices]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleDayToggle = (serviceId, dayIndex, isClosed) => {
    setHoursData(prevData => {
      const newData = { ...prevData };
      if (!newData[serviceId]) newData[serviceId] = {};
      
      newData[serviceId][dayIndex] = isClosed 
        ? { is_closed_all_day: true, intervals: [] }
        : { is_closed_all_day: false, intervals: [{ start_time: '09:00', end_time: '17:00', crosses_midnight: false }] };
      
      return newData;
    });
    
    setHasChanges(true);
    onHoursChange?.(true);
  };

  const handleTimeChange = (serviceId, dayIndex, field, value) => {
    setHoursData(prevData => {
      const newData = { ...prevData };
      if (!newData[serviceId]) newData[serviceId] = {};
      if (!newData[serviceId][dayIndex]) {
        newData[serviceId][dayIndex] = {
          intervals: [{ start_time: '09:00', end_time: '17:00', crosses_midnight: false }],
          is_closed_all_day: false
        };
      }
      
      const dayData = newData[serviceId][dayIndex];
      if (!dayData.intervals?.length) {
        dayData.intervals = [{ start_time: '09:00', end_time: '17:00', crosses_midnight: false }];
      }
      
      dayData.intervals[0] = { ...dayData.intervals[0], [field]: value };
      dayData.intervals[0].crosses_midnight = dayData.intervals[0].start_time > dayData.intervals[0].end_time;
      
      return newData;
    });
    
    setHasChanges(true);
    onHoursChange?.(true);
  };

  const getDayData = (serviceId, dayIndex) => 
    hoursData[serviceId]?.[dayIndex] || { intervals: [], is_closed_all_day: true };

  const getPrimaryInterval = (serviceId, dayIndex) => {
    const dayData = getDayData(serviceId, dayIndex);
    return dayData.intervals?.[0] || { start_time: '09:00', end_time: '17:00', crosses_midnight: false };
  };

  const renderServiceHours = ({ serviceId, serviceLabel }) => {
    const isServiceAvailable = activeServiceIdSet.has(serviceId);
    
    if (!isServiceAvailable) {
      return (
        <Paper className={classes.contactCard} elevation={0}>
          <Typography variant="h6" style={{ color: '#666', marginBottom: '10px' }}>
            {serviceLabel} Service Not Available
          </Typography>
          <Typography variant="body2" style={{ color: '#888', marginBottom: '20px' }}>
            This service is not currently enabled for your location.
          </Typography>
          <Button variant="contained" style={{ backgroundColor: '#0099b9', color: 'white' }}>
            Contact Us to Add This Service
          </Button>
        </Paper>
      );
    }

    const desktopView = (
      <div>
        <div className={`${classes.hoursGrid} ${classes.hoursGridHeader}`}>
          <div>Day</div>
          <div>Open</div>
          <div>Close</div>
          <div>Closed</div>
        </div>
        
        {dayNames.map((dayName, dayIndex) => {
          const dayData = getDayData(serviceId, dayIndex);
          const primaryInterval = getPrimaryInterval(serviceId, dayIndex);
          const isClosed = dayData.is_closed_all_day;
          const hasMultipleIntervals = dayData.intervals?.length > 1;
          
          return (
            <div key={dayIndex}>
              <div className={classes.hoursGrid}>
                <div className={classes.dayLabel}>{dayName.substring(0, 3)}</div>
                <TextField
                  type="time"
                  size="small"
                  variant="outlined"
                  className={classes.timeField}
                  value={isClosed ? '' : primaryInterval.start_time}
                  disabled={isClosed}
                  onChange={(e) => handleTimeChange(serviceId, dayIndex, 'start_time', e.target.value)}
                />
                <TextField
                  type="time"
                  size="small"
                  variant="outlined"
                  className={classes.timeField}
                  value={isClosed ? '' : primaryInterval.end_time}
                  disabled={isClosed}
                  onChange={(e) => handleTimeChange(serviceId, dayIndex, 'end_time', e.target.value)}
                />
                <Checkbox
                  checked={isClosed}
                  onChange={(e) => handleDayToggle(serviceId, dayIndex, e.target.checked)}
                  color="primary"
                  size="small"
                />
              </div>
              {hasMultipleIntervals && !isClosed && (
                <div className={classes.intervalNote}>
                  * This day has {dayData.intervals.length} time intervals. Only the first is shown.
                </div>
              )}
              {primaryInterval.crosses_midnight && !isClosed && (
                <div className={classes.intervalNote}>* Crosses midnight</div>
              )}
            </div>
          );
        })}
      </div>
    );

    const mobileView = (
      <div className={classes.mobileContainer}>
        {dayNames.map((dayName, dayIndex) => {
          const dayData = getDayData(serviceId, dayIndex);
          const primaryInterval = getPrimaryInterval(serviceId, dayIndex);
          const isClosed = dayData.is_closed_all_day;
          const hasMultipleIntervals = dayData.intervals?.length > 1;
          
          return (
            <Paper key={dayIndex} className={classes.dayCard} elevation={0}>
              <div className={classes.dayCardHeader}>
                <Typography className={classes.dayCardTitle}>{dayName}</Typography>
                <Checkbox
                  checked={isClosed}
                  onChange={(e) => handleDayToggle(serviceId, dayIndex, e.target.checked)}
                  color="primary"
                />
              </div>
              
              {isClosed ? (
                <Typography className={classes.closedLabel}>Closed</Typography>
              ) : (
                <div className={classes.timeRange}>
                  <TextField
                    type="time"
                    variant="outlined"
                    className={classes.timeFieldMobile}
                    value={primaryInterval.start_time}
                    onChange={(e) => handleTimeChange(serviceId, dayIndex, 'start_time', e.target.value)}
                  />
                  <span className={classes.timeSeparator}>to</span>
                  <TextField
                    type="time"
                    variant="outlined"
                    className={classes.timeFieldMobile}
                    value={primaryInterval.end_time}
                    onChange={(e) => handleTimeChange(serviceId, dayIndex, 'end_time', e.target.value)}
                  />
                </div>
              )}
              
              {hasMultipleIntervals && !isClosed && (
                <Typography className={classes.intervalNote} style={{ marginTop: '8px' }}>
                  * This day has {dayData.intervals.length} time intervals. Only the first is shown.
                </Typography>
              )}
              {primaryInterval.crosses_midnight && !isClosed && (
                <Typography className={classes.intervalNote} style={{ marginTop: '8px' }}>
                  * Crosses midnight
                </Typography>
              )}
            </Paper>
          );
        })}
      </div>
    );

    return <>{desktopView}{mobileView}</>;
  };

  React.useImperativeHandle(ref, () => ({
    getHoursData: () => {
      const availableHoursData = {};
      const activeIds = Array.from(activeServiceIdSet);

      activeIds.forEach((serviceId) => {
        if (hoursData[serviceId]) {
          availableHoursData[serviceId] = hoursData[serviceId];
        }
      });

      return availableHoursData;
    },
    hasChanges: () => hasChanges,
    resetChanges: () => setHasChanges(false),
  }));

  return (
    <div>
      <Typography variant="h6" style={{ marginBottom: '15px', color: '#333' }}>
        Hours of Operation
      </Typography>
      
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className={classes.tabsRoot}
        indicatorColor="primary"
        variant="fullWidth"
      >
        {availableServices.map(({ id, name }) => (
          <Tab key={id} label={name} className={classes.tab} />
        ))}
      </Tabs>

      {availableServices.map(({ id, name }, index) => (
        <TabPanel key={id} value={activeTab} index={index}>
          {renderServiceHours({ serviceId: id, serviceLabel: name })}
        </TabPanel>
      ))}
    </div>
  );
});

export default HoursManagement;